import { CheckCheck, FileText } from 'lucide-react';
import { useState } from 'react';
import {
  type ImportListRow,
  type ImportListRows,
  useDtoolsProjectRows,
  usePortalioProposalRows,
} from 'src/components/ImportProposalsModal/use-import-list-rows';
import { ModalList, type ModalListItem } from 'src/components/ModalContainer';

const SELECT_ALL_ROW_ID = 'select-all';

// D-Tools searches on the server, so typing waits for a pause before it asks again.
const DTOOLS_SEARCH_DEBOUNCE_MS = 200;

export interface ImportRowsProps {
  selected: ImportListRow[];
  onSelectedChange: (selected: ImportListRow[]) => void;
  /** Adds a first row that selects, or clears, every row loaded so far. */
  withSelectAll?: boolean;
}

const toggleRow = (selected: ImportListRow[], row: ImportListRow) =>
  selected.some(({ id }) => id === row.id)
    ? selected.filter(({ id }) => id !== row.id)
    : [...selected, row];

const ImportRows = ({
  list,
  selected,
  onSelectedChange,
  withSelectAll = false,
  onSearchChange,
  searchDebounce,
}: ImportRowsProps & {
  list: ImportListRows;
  onSearchChange: (search: string) => void;
  searchDebounce?: number;
}) => {
  const selectedIds = new Set(selected.map(({ id }) => id));
  const rowById = new Map(list.rows.map((row) => [String(row.id), row]));
  const allSelected = list.rows.length > 0 && list.rows.every(({ id }) => selectedIds.has(id));

  const selectAllRow: ModalListItem[] =
    withSelectAll && list.rows.length > 0
      ? [
          {
            id: SELECT_ALL_ROW_ID,
            title: 'Select all visible',
            icon: <CheckCheck />,
            checked: allSelected,
          },
        ]
      : [];

  const handleSelect = ({ id }: ModalListItem) => {
    if (id === SELECT_ALL_ROW_ID) {
      const loadedIds = new Set(list.rows.map((row) => row.id));
      const others = selected.filter((row) => !loadedIds.has(row.id));
      onSelectedChange(allSelected ? others : [...others, ...list.rows]);
      return;
    }
    const row = rowById.get(id);
    if (row) {
      onSelectedChange(toggleRow(selected, row));
    }
  };

  return (
    <ModalList
      items={[
        ...selectAllRow,
        ...list.rows.map((row) => ({
          id: String(row.id),
          title: row.name,
          description: row.description,
          icon: <FileText />,
          checked: selectedIds.has(row.id),
        })),
      ]}
      shouldFilter={false}
      onSearchChange={(search) => onSearchChange(search.toLowerCase())}
      searchDebounce={searchDebounce}
      onSelect={handleSelect}
      loading={list.loading}
      loadMore={
        list.hasMore
          ? { onSelect: () => void list.loadMore(), loading: list.loadingMore }
          : undefined
      }
      placeholder="Search"
      emptyText="Nothing matches."
      testId="import-proposals"
    />
  );
};

/** portal.io proposals as a multi-select list part. */
export const PortalioImportRows = (props: ImportRowsProps) => {
  const [search, setSearch] = useState('');
  const list = usePortalioProposalRows(search);
  return <ImportRows {...props} list={list} onSearchChange={setSearch} />;
};

/** D-Tools Cloud projects as a multi-select list part. */
export const DtoolsImportRows = (props: ImportRowsProps) => {
  const [search, setSearch] = useState('');
  const list = useDtoolsProjectRows(search);
  return (
    <ImportRows
      {...props}
      list={list}
      onSearchChange={setSearch}
      searchDebounce={DTOOLS_SEARCH_DEBOUNCE_MS}
    />
  );
};
